import { ConfigProvider, Layout, Typography, Button, Card, Tag, Space, theme } from 'antd';
import {
  LoginOutlined, LogoutOutlined, ShopOutlined, ShoppingCartOutlined,
  ClusterOutlined, AppstoreOutlined, FileTextOutlined, CheckCircleFilled,
  EuroCircleOutlined,
} from '@ant-design/icons';
import keycloak from './keycloak';
import deDE from 'antd/locale/de_DE';

const { Header, Content, Footer } = Layout;
const { Title, Text, Paragraph } = Typography;

function hasRole(resourceAccess, clientId, names) {
  const roles = resourceAccess?.[clientId]?.roles || [];
  return names.some((n) => roles.includes(n));
}

function HubLogo({ size = 36 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
      <rect width="100" height="100" rx="22" fill="#2c5a66" />
      <rect x="14" y="14" width="30" height="30" rx="8" fill="rgba(255,255,255,0.35)" />
      <rect x="56" y="14" width="30" height="30" rx="8" fill="#ffffff" />
      <rect x="14" y="56" width="30" height="30" rx="8" fill="rgba(255,255,255,0.35)" />
      <rect x="56" y="56" width="30" height="30" rx="8" fill="rgba(255,255,255,0.35)" />
    </svg>
  );
}

const TILES = [
  {
    key: 'fairshop',
    title: 'Fairshop',
    subtitle: 'fairshop.fairweben.de',
    description: 'Speisekarte durchsuchen und als Kunde bestellen.',
    url: 'https://fairshop.fairweben.de',
    icon: <ShopOutlined />,
    access: () => true,
    accessLabel: 'Öffentlich',
  },
  {
    key: 'theke',
    title: 'Theken-UI',
    subtitle: 'orders.fairweben.de',
    description: 'Eingehende Bestellungen annehmen und abschließen.',
    url: 'https://orders.fairweben.de',
    icon: <ShoppingCartOutlined />,
    access: (realmRoles) => realmRoles.includes('cook') || realmRoles.includes('rider'),
    accessLabel: 'Rolle: cook / rider',
  },
  {
    key: 'stores',
    title: 'fair_stores',
    subtitle: 'stores.fairweben.de',
    description: 'Store-Stammdaten, Öffnungszeiten und Lieferanten pflegen.',
    url: 'https://stores.fairweben.de',
    icon: <ClusterOutlined />,
    access: (_realmRoles, resourceAccess) => hasRole(resourceAccess, 'fair-stores-app', ['manager', 'support']),
    accessLabel: 'Rolle: manager / support',
  },
  {
    key: 'products',
    title: 'fair_products',
    subtitle: 'products.fairweben.de',
    description: 'Kategorien, Produkte und Preise pflegen.',
    url: 'https://products.fairweben.de',
    icon: <AppstoreOutlined />,
    access: (_realmRoles, resourceAccess) => hasRole(resourceAccess, 'fair-products-app', ['manager', 'support']),
    accessLabel: 'Rolle: manager / support',
  },
  {
    key: 'invoices',
    title: 'fair_invoices',
    subtitle: 'invoices.fairweben.de',
    description: 'Rechnungen an Stores erstellen (Umsatz abzüglich Provision).',
    url: 'https://invoices.fairweben.de',
    icon: <EuroCircleOutlined />,
    access: (_realmRoles, resourceAccess) => hasRole(resourceAccess, 'fair-invoices-app', ['manager', 'buchhalter']),
    accessLabel: 'Rolle: manager / buchhalter',
  },
  {
    key: 'docs',
    title: 'Dokumentation',
    subtitle: 'Systemarchitektur',
    description: 'API- und Datenbank-Übersicht der gesamten Fairweben-Landschaft.',
    url: '/docs/architektur.html',
    icon: <FileTextOutlined />,
    access: () => true,
    accessLabel: 'Öffentlich',
  },
];

export default function App({ authenticated }) {
  const realmRoles = keycloak.realmAccess?.roles || [];
  const resourceAccess = keycloak.resourceAccess || {};
  const displayName = keycloak.tokenParsed?.preferred_username;

  return (
    <ConfigProvider
      locale={deDE}
      theme={{
        algorithm: theme.defaultAlgorithm,
        token: {
          colorPrimary: '#3a6b7a',
          colorLink: '#3a6b7a',
          colorBgLayout: '#eef2f2',
          colorBgContainer: '#ffffff',
          colorBorder: '#d7e0e0',
          borderRadius: 10,
        },
      }}
    >
      <Layout style={{ minHeight: '100vh', background: '#eef2f2' }}>
        <Header style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 24px', background: '#233b42',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <HubLogo size={32} />
            <Title level={4} style={{ color: '#fff', margin: 0, lineHeight: 1 }}>Fair Hub</Title>
          </div>
          {authenticated ? (
            <Space>
              <Text style={{ color: '#cfe0e0' }}>{displayName}</Text>
              <Button ghost icon={<LogoutOutlined />} onClick={() => keycloak.logout()}
                style={{ borderColor: '#fff', color: '#fff' }}>
                Abmelden
              </Button>
            </Space>
          ) : (
            <Button ghost icon={<LoginOutlined />} onClick={() => keycloak.login()}
              style={{ borderColor: '#fff', color: '#fff' }}>
              Anmelden
            </Button>
          )}
        </Header>

        <Content style={{ padding: '32px 24px', maxWidth: 1200, margin: '0 auto', width: '100%' }}>
          <Paragraph style={{ color: '#4a5c60', maxWidth: '60ch' }}>
            {authenticated
              ? 'Angemeldet — Kacheln, für die du berechtigt bist, sind hervorgehoben.'
              : 'Nicht angemeldet — melde dich an, um zu sehen, für welche Apps du berechtigt bist.'}
          </Paragraph>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 20,
            marginTop: 16,
          }}>
            {TILES.map((tile) => {
              const granted = authenticated && tile.access(realmRoles, resourceAccess);
              return (
                <Card
                  key={tile.key}
                  hoverable
                  onClick={() => window.open(tile.url, '_blank', 'noopener')}
                  style={{
                    borderColor: granted ? '#3a6b7a' : '#d7e0e0',
                    borderWidth: granted ? 2 : 1,
                    boxShadow: granted ? '0 4px 16px -6px rgba(58,107,122,0.35)' : 'none',
                    cursor: 'pointer',
                  }}
                >
                  <Space align="start" style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Space direction="vertical" size={0}>
                      <Space size={8}>
                        <span style={{ fontSize: 20, color: '#3a6b7a' }}>{tile.icon}</span>
                        <Title level={5} style={{ margin: 0 }}>{tile.title}</Title>
                      </Space>
                      <Text type="secondary" style={{ fontSize: 12 }}>{tile.subtitle}</Text>
                    </Space>
                    {granted && <CheckCircleFilled style={{ color: '#3a6b7a', fontSize: 18 }} />}
                  </Space>
                  <Paragraph style={{ margin: '12px 0 8px', fontSize: 13, color: '#5a6b6e' }}>
                    {tile.description}
                  </Paragraph>
                  <Tag color={granted ? 'cyan' : 'default'}>{tile.accessLabel}</Tag>
                </Card>
              );
            })}
          </div>
        </Content>

        <Footer style={{ textAlign: 'center', color: '#7a8a8c', background: 'transparent' }}>
          Fair Hub by <a href="https://open.fairweben.de" target="_blank" rel="noopener noreferrer" style={{ color: '#3a6b7a' }}>Fairweben</a>
        </Footer>
      </Layout>
    </ConfigProvider>
  );
}
